import React, { useEffect, useMemo, useState } from "react";
import "../styles/Pages.css";
import "../styles/DailyDetailEditor.css";

const BASE_URL = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
const DATA_URL = `${BASE_URL}/dailydetail/dailydetail-11.json`;
const SAVE_URL_CANDIDATES = Array.from(
  new Set(
    [
      `${BASE_URL}/dailydetail/update-dailydetail.php`,
      "/dailydetail/update-dailydetail.php",
      "dailydetail/update-dailydetail.php",
      "./dailydetail/update-dailydetail.php",
    ].filter(Boolean),
  ),
);

const FIELD_ORDER = [
  "infoid",
  "infodate",
  "infoyear",
  "infomonth",
  "infoday",
  "place",
  "city",
  "shortdescguj",
  "pradesh",
  "flagpadharamni",
  "flagsabha",
  "flagthal",
  "videourl",
  "photourl",
  "flagpatrikaapp",
  "flagparamrutapp",
  "flagpatrika",
  "latitude",
  "longitude",
  "highlighturl",
  "clipurl1",
  "clipurl2",
  "village",
  "Pratishtha",
  "Shibir",
  "StaffSabha",
  "Utsav",
];

const NUMERIC_FIELDS = new Set([
  "infoid",
  "infoyear",
  "infomonth",
  "infoday",
  "flagpadharamni",
  "flagsabha",
  "flagthal",
  "flagpatrikaapp",
  "flagparamrutapp",
  "flagpatrika",
  "latitude",
  "longitude",
  "Pratishtha",
  "Shibir",
  "StaffSabha",
  "Utsav",
]);

function getDefaultRecord(nextId) {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const day = now.getDate();

  return {
    infoid: nextId,
    infodate: `${year}/${String(month).padStart(2, "0")}/${String(day).padStart(2, "0")}`,
    infoyear: year,
    infomonth: month,
    infoday: day,
    place: "",
    city: "",
    shortdescguj: "",
    pradesh: "",
    flagpadharamni: 0,
    flagsabha: 0,
    flagthal: 0,
    videourl: "NULL",
    photourl: "NULL",
    flagpatrikaapp: 0,
    flagparamrutapp: 0,
    flagpatrika: 0,
    latitude: 0,
    longitude: 0,
    highlighturl: "",
    clipurl1: "",
    clipurl2: "",
    village: "",
    Pratishtha: 0,
    Shibir: 0,
    StaffSabha: 0,
    Utsav: 0,
  };
}

function DailyDetailEditor() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [saveKey, setSaveKey] = useState("");
  const [saveState, setSaveState] = useState({ type: "", message: "" });
  const [saving, setSaving] = useState(false);
  const [activeSaveUrl, setActiveSaveUrl] = useState("");
  const [phpCheck, setPhpCheck] = useState({
    checking: true,
    ok: false,
    message: "Checking save endpoint...",
  });

  useEffect(() => {
    fetch(DATA_URL)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load dailydetail-11.json");
        return res.json();
      })
      .then((data) => {
        if (!Array.isArray(data)) throw new Error("JSON root must be an array");
        setRecords(data);
        setSelectedId(data[0]?.infoid ?? null);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message || "Could not load data");
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const checkPhpEndpoint = async () => {
      setPhpCheck({
        checking: true,
        ok: false,
        message: "Checking save endpoint...",
      });

      for (const saveUrl of SAVE_URL_CANDIDATES) {
        try {
          const res = await fetch(saveUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ authKey: "__probe__", records: [] }),
          });

          const raw = await res.text();
          let parsed = null;
          if (raw) {
            try {
              parsed = JSON.parse(raw);
            } catch {
              parsed = null;
            }
          }

          const isPostReachable =
            parsed && typeof parsed === "object" && res.status !== 404;

          if (isPostReachable) {
            if (!cancelled) {
              setActiveSaveUrl(saveUrl);
              setPhpCheck({
                checking: false,
                ok: true,
                message: `Save endpoint is POST-ready: ${saveUrl}`,
              });
            }
            return;
          }
        } catch {
          // Try next candidate.
        }
      }

      if (!cancelled) {
        setActiveSaveUrl("");
        setPhpCheck({
          checking: false,
          ok: false,
          message:
            "Save endpoint is not available. Check PHP/Nginx config for update-dailydetail.php.",
        });
      }
    };

    checkPhpEndpoint();

    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return records;
    return records.filter((r) => {
      const bag = [
        r.infoid,
        r.infodate,
        r.city,
        r.place,
        r.pradesh,
        r.shortdescguj,
      ]
        .join(" ")
        .toLowerCase();
      return bag.includes(q);
    });
  }, [records, search]);

  const selectedIndex = records.findIndex((r) => r.infoid === selectedId);
  const selected = selectedIndex >= 0 ? records[selectedIndex] : null;

  const setField = (field, value) => {
    if (selectedIndex < 0) return;
    setRecords((prev) => {
      const next = [...prev];
      const current = { ...next[selectedIndex] };
      current[field] = value;
      next[selectedIndex] = current;
      return next;
    });
  };

  const parseFieldValue = (field, raw) => {
    if (!NUMERIC_FIELDS.has(field)) return raw;
    if (raw === "") return 0;
    const num = Number(raw);
    return Number.isNaN(num) ? 0 : num;
  };

  const addRecord = () => {
    const maxId = records.reduce(
      (m, r) => Math.max(m, Number(r.infoid) || 0),
      0,
    );
    const nextRecord = getDefaultRecord(maxId + 1);
    setRecords((prev) => [...prev, nextRecord]);
    setSelectedId(nextRecord.infoid);
    setSaveState({ type: "", message: "" });
  };

  const deleteRecord = () => {
    if (selectedIndex < 0) return;
    const next = records.filter((_, idx) => idx !== selectedIndex);
    setRecords(next);
    setSelectedId(next[0]?.infoid ?? null);
    setSaveState({ type: "", message: "" });
  };

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(records, null, 2)], {
      type: "application/json;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "dailydetail-11.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const saveToServer = async () => {
    if (!phpCheck.ok || !activeSaveUrl) {
      setSaveState({
        type: "error",
        message:
          "Save endpoint is not ready yet. Wait for PHP check or fix server config.",
      });
      return;
    }

    if (!saveKey.trim()) {
      setSaveState({
        type: "error",
        message: "Enter server save passcode before saving.",
      });
      return;
    }

    setSaving(true);
    setSaveState({ type: "", message: "" });

    let lastErrorMessage = "Could not save";
    try {
      const preferredUrls = [
        activeSaveUrl,
        ...SAVE_URL_CANDIDATES.filter((url) => url !== activeSaveUrl),
      ];

      for (const saveUrl of preferredUrls) {
        try {
          const res = await fetch(saveUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ authKey: saveKey, records }),
          });

          const raw = await res.text();
          let result = {};
          if (raw) {
            try {
              result = JSON.parse(raw);
            } catch {
              const preview = raw.replace(/\s+/g, " ").trim().slice(0, 180);
              if (res.status === 404 || res.status === 405) {
                lastErrorMessage = `Save endpoint blocked at ${saveUrl} (HTTP ${res.status}).`;
                continue;
              }
              throw new Error(
                `Server returned non-JSON response from ${saveUrl} (HTTP ${res.status}). ${preview || "Check PHP errors/logs."}`,
              );
            }
          }

          if (res.status === 404 || res.status === 405) {
            lastErrorMessage = `Save endpoint blocked at ${saveUrl} (HTTP ${res.status}).`;
            continue;
          }

          if (!res.ok || !result.success) {
            const message =
              result.message ||
              `Save failed on ${saveUrl} (HTTP ${res.status}${
                res.statusText ? ` ${res.statusText}` : ""
              })`;

            if (res.status !== 404 && res.status !== 405) {
              const fatalError = new Error(message);
              fatalError.stopRetries = true;
              throw fatalError;
            }

            lastErrorMessage = message;
            continue;
          }

          setSaveState({
            type: "success",
            message: `Saved ${result.count ?? records.length} records to server.`,
          });
          return;
        } catch (e) {
          lastErrorMessage = e.message || "Could not save";
          if (e.stopRetries) {
            throw e;
          }
        }
      }

      throw new Error(`${lastErrorMessage} Tried: ${preferredUrls.join(", ")}`);
    } catch (e) {
      setSaveState({ type: "error", message: e.message || "Could not save" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="page-content">
      <section className="page-header">
        <h1 className="section-title">Dailydetail Editor</h1>
        <p className="header-subtitle">Edit and save dailydetail-11.json</p>
      </section>

      <section className="page-body dailydetail-admin">
        <div className="editor-toolbar">
          <input
            type="text"
            className="editor-search"
            placeholder="Search by id, date, city, place..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button onClick={addRecord}>Add Record</button>
          <button onClick={deleteRecord} disabled={!selected}>
            Delete Selected
          </button>
          <button onClick={downloadJson}>Download JSON</button>
        </div>

        <div className="editor-save-row">
          <input
            type="password"
            placeholder="Server save passcode"
            value={saveKey}
            onChange={(e) => setSaveKey(e.target.value)}
            className="save-key-input"
          />
          <button
            onClick={saveToServer}
            disabled={
              saving || !records.length || phpCheck.checking || !phpCheck.ok
            }
          >
            {saving ? "Saving..." : "Save To Server"}
          </button>
          <span className={`save-status ${saveState.type}`}>
            {saveState.message}
          </span>
        </div>

        <p className={`editor-note ${phpCheck.ok ? "" : "save-status error"}`}>
          {phpCheck.message}
        </p>

        <p className="editor-note">
          Note: Local development server cannot execute PHP. Use this page on
          your live PHP hosting to save directly.
        </p>

        {loading && <p>Loading...</p>}
        {error && <p className="save-status error">{error}</p>}

        {!loading && !error && (
          <div className="editor-layout">
            <aside className="record-list-panel">
              <h3>Records ({filtered.length})</h3>
              <ul className="record-list">
                {filtered.map((record) => {
                  const active = record.infoid === selectedId;
                  return (
                    <li key={record.infoid}>
                      <button
                        className={`record-item ${active ? "active" : ""}`}
                        onClick={() => setSelectedId(record.infoid)}
                      >
                        <span>#{record.infoid}</span>
                        <span>{record.infodate}</span>
                        <span>{record.city || "-"}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>

            <section className="record-form-panel">
              {selected ? (
                <>
                  <h3>Edit Record #{selected.infoid}</h3>
                  <div className="record-form-grid">
                    {FIELD_ORDER.map((field) => {
                      const value = selected[field] ?? "";
                      const isLongText = field === "shortdescguj";

                      return (
                        <label key={field} className="record-field">
                          <span>{field}</span>
                          {isLongText ? (
                            <textarea
                              value={String(value)}
                              rows={5}
                              onChange={(e) =>
                                setField(
                                  field,
                                  parseFieldValue(field, e.target.value),
                                )
                              }
                            />
                          ) : (
                            <input
                              type={
                                NUMERIC_FIELDS.has(field) ? "number" : "text"
                              }
                              value={String(value)}
                              onChange={(e) =>
                                setField(
                                  field,
                                  parseFieldValue(field, e.target.value),
                                )
                              }
                            />
                          )}
                        </label>
                      );
                    })}
                  </div>
                </>
              ) : (
                <p>Select a record to edit.</p>
              )}
            </section>
          </div>
        )}
      </section>
    </main>
  );
}

export default DailyDetailEditor;
